import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { 
  PlayIcon, 
  StopIcon, 
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Helper function to format uptime
const formatUptime = (seconds) => {
  if (!seconds) return '--';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return `${hours}h ${minutes}m ${secs}s`;
};

const BotControls = ({ 
  isRunning, 
  onStart, 
  onStop, 
  onRestart, 
  showRestart = false,
  error: propError 
}) => {
  const [status, setStatus] = useState(isRunning ? 'running' : 'stopped');
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [uptime, setUptime] = useState(0);
  const [error, setError] = useState(propError || '');
  const uptimeInterval = useRef(null);
  const startTime = useRef(null);
  
  // Update status when isRunning prop changes
  useEffect(() => {
    const newStatus = isRunning ? 'running' : 'stopped';
    setStatus(newStatus);
    setLastUpdated(new Date());
    
    // Handle uptime counter
    if (isRunning && !uptimeInterval.current) {
      startTime.current = new Date();
      uptimeInterval.current = setInterval(() => {
        setUptime(Math.floor((new Date() - startTime.current) / 1000));
      }, 1000);
    } else if (!isRunning && uptimeInterval.current) {
      clearInterval(uptimeInterval.current);
      uptimeInterval.current = null;
      setUptime(0);
    }
    
    return () => {
      if (uptimeInterval.current) {
        clearInterval(uptimeInterval.current);
      }
    };
  }, [isRunning]);
  
  // Update error state when prop changes
  useEffect(() => {
    if (propError) {
      setError(propError);
    }
  }, [propError]);

  const handleStart = async () => {
    setStatus('starting');
    setError('');
    try {
      const result = await onStart();
      if (result?.success === false) {
        throw new Error(result.message || 'Failed to start bot');
      }
      setStatus('running');
      setLastUpdated(new Date());
      toast.success('Bot started successfully');
      setError('');
    } catch (error) {
      console.error('Error starting bot:', error);
      setStatus('stopped');
      const errorMsg = error.message || 'Failed to start bot';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleStop = async () => {
    setStatus('stopping');
    setError('');
    try {
      const result = await onStop();
      if (result?.success === false) {
        throw new Error(result.message || 'Failed to stop bot');
      }
      setStatus('stopped');
      setLastUpdated(new Date());
      toast.success('Bot stopped successfully');
      setError('');
    } catch (error) {
      console.error('Error stopping bot:', error);
      setStatus('running');
      const errorMsg = error.message || 'Failed to stop bot';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleRestart = async () => {
    setStatus('restarting');
    setError('');
    try {
      if (onRestart) {
        const result = await onRestart();
        if (result?.success === false) {
          throw new Error(result.message || 'Failed to restart bot');
        }
      } else {
        // Fallback to stop/start if onRestart not provided
        await onStop();
        await new Promise(resolve => setTimeout(resolve, 1000));
        await onStart();
      }
      setStatus('running');
      setLastUpdated(new Date());
      toast.success('Bot restarted successfully');
      setError('');
    } catch (error) {
      console.error('Error during restart:', error);
      const newStatus = isRunning ? 'running' : 'stopped';
      setStatus(newStatus);
      const errorMsg = error.message || 'Failed to restart bot';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const getStatusConfig = () => {
    const config = {
      running: {
        color: 'text-emerald-500',
        bgColor: 'bg-emerald-50 dark:bg-emerald-900/30',
        icon: CheckCircleIcon,
        text: 'Bot is running',
        button: {
          text: 'Stop Bot',
          loadingText: 'Stopping...',
          icon: StopIcon,
          color: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          disabled: false
        }
      },
      stopped: {
        color: 'text-red-500',
        bgColor: 'bg-red-50 dark:bg-red-900/30',
        icon: ExclamationCircleIcon,
        text: 'Bot is stopped',
        button: {
          text: 'Start Bot',
          loadingText: 'Starting...',
          icon: PlayIcon,
          color: 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500',
          disabled: false
        }
      },
      starting: {
        color: 'text-amber-500',
        bgColor: 'bg-amber-50 dark:bg-amber-900/30',
        icon: ClockIcon,
        text: 'Starting bot...',
        button: {
          text: 'Start Bot',
          loadingText: 'Starting...',
          icon: PlayIcon,
          color: 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500',
          disabled: true
        }
      },
      stopping: {
        color: 'text-amber-500',
        bgColor: 'bg-amber-50 dark:bg-amber-900/30',
        icon: ClockIcon,
        text: 'Stopping bot...',
        button: {
          text: 'Stop Bot',
          loadingText: 'Stopping...',
          icon: StopIcon,
          color: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          disabled: true
        }
      },
      restarting: {
        color: 'text-amber-500',
        bgColor: 'bg-amber-50 dark:bg-amber-900/30',
        icon: ArrowPathIcon,
        text: 'Restarting bot...',
        button: {
          text: 'Restart Bot',
          loadingText: 'Restarting...',
          icon: ArrowPathIcon,
          color: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
          disabled: true
        }
      }
    };

    return config[status] || config.stopped;
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;
  const ButtonIcon = statusConfig.button.icon;
  const isButtonLoading = ['starting', 'stopping', 'restarting'].includes(status);

  // Display error message if present
  const renderError = () => {
    if (!error) return null;
    
    return (
      <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md flex items-start">
        <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="card overflow-hidden">
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Bot Controls</h2>
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusConfig.color} ${statusConfig.bgColor}`}>
              <StatusIcon className={`h-4 w-4 mr-1.5 ${status === 'restarting' ? 'animate-spin' : ''}`} />
              {statusConfig.text}

            </div>

            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
            {renderError()}
          </div>
          
          <div className="flex space-x-3">
            {/* Main Action Button (Start/Stop) */}
            <button
              type="button"
              onClick={isRunning ? handleStop : handleStart}
              disabled={isButtonLoading}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${statusConfig.button.color} focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isButtonLoading ? (
                <>
                  <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  {statusConfig.button.loadingText}
                </>
              ) : (
                <>
                  <ButtonIcon className="-ml-1 mr-2 h-4 w-4" />
                  {statusConfig.button.text}
                </>
              )}
            </button>

            {/* Restart Button - Only show when bot is running and not in loading state */}
            {isRunning && status === 'running' && (
              <button
                type="button"
                onClick={handleRestart}
                disabled={isButtonLoading}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowPathIcon className="-ml-1 mr-2 h-4 w-4" />
                Restart
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Status Details */}
      <div className="bg-gray-50 dark:bg-gray-800/50 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-sm">
            <p className="text-gray-500 dark:text-gray-400">Status</p>
            <p className={`font-medium ${statusConfig.color}`}>{statusConfig.text}</p>
          </div>
          <div className="text-sm">
            <p className="text-gray-500 dark:text-gray-400">Last Update</p>
            <p className="text-gray-900 dark:text-gray-200">{lastUpdated.toLocaleString()}</p>
          </div>
          <div className="text-sm">
            <p className="text-gray-500 dark:text-gray-400">Uptime</p>
            <p className="text-gray-900 dark:text-gray-200">
              {status === 'running' ? 'Active' : 'Inactive'}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Bot Status</h3>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="bg-white dark:bg-gray-700 overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">Current State</dt>
              <dd className={`mt-1 text-2xl font-semibold ${statusConfig.color}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </dd>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-700 overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">Uptime</dt>
              <dd className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {status === 'running' ? formatUptime(uptime) : '--'}
              </dd>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-700 overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">Last Updated</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                {lastUpdated.toLocaleString()}
              </dd>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BotControls;
